import { Briefcase, MessageSquare, Target, CheckCircle2 } from 'lucide-react';

export default function SimulatedClient() {
  const features = [
    {
      icon: <Briefcase className="w-6 h-6 text-primary-600 dark:text-primary-500" />,
      title: "بريف (Brief) مسعر وحقيقي",
      desc: "ستحصل على بريف مفصل يحاكي متطلبات المشروع كما في السوق الاحترافي تماماً."
    },
    {
      icon: <Target className="w-6 h-6 text-primary-600 dark:text-primary-500" />,
      title: "إدارة التعديلات باحترافية",
      desc: "ستخوض تجربة استلام الملاحظات وكيفية إدارة التعديلات والنقاشات مع العميل بذكاء."
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-primary-600 dark:text-primary-500" />,
      title: "مواجهة الرفض والتحسين",
      desc: "لتكن مستعداً للرفض؛ سنعلمك كيفية استيعابه وتحسين عملك للوصول إلى النتيجة المبهرة."
    }
  ];

  return (
    <section id="features" className="py-24 relative overflow-hidden bg-white dark:bg-zinc-950 transition-colors duration-500 border-b border-slate-200 dark:border-white/5">
      {/* Background glow */}
      <div className="absolute top-1/2 right-0 -mr-40 bg-primary-100 dark:bg-primary-900/20 rounded-full w-[500px] h-[500px] blur-[150px] opacity-50 dark:opacity-30 pointer-events-none transition-colors duration-500"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <div className="lg:w-1/2">
            <h2 className="text-lg font-bold text-primary-600 dark:text-primary-500 mb-3 uppercase tracking-widest flex items-center gap-2 transition-colors duration-500">
              <span className="w-8 h-1 bg-primary-500 rounded-full"></span>
              ميزتنا التنافسية
            </h2>
            <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6 leading-[1.3] transition-colors duration-500">
              عِش واقع السوق قبل <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-600">أن تدخله</span>
            </h3>
            <p className="text-2xl text-slate-600 dark:text-zinc-400 mb-10 leading-relaxed font-light transition-colors duration-500">
              تميز دورتنا بتقديم تجربة "العميل المحاكى" الفريدة. ستحصل على بريف (Brief) حقيقي، وستخوض تجربة استلام الملاحظات، وإدارة التعديلات، وحتى مواجهة الرفض والتحسين، تماماً كما يحدث في مشاريعك الحقيقية.
            </p>

            <div className="space-y-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex gap-5 items-start group">
                  <div className="bg-slate-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-slate-200 dark:border-white/10 shrink-0 shadow-sm dark:shadow-md group-hover:border-primary-500/50 group-hover:bg-primary-50 dark:group-hover:bg-primary-500/20 transition-all duration-300">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{feature.title}</h4>
                    <p className="text-slate-600 dark:text-zinc-400 leading-relaxed text-xl md:text-xl transition-colors">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-1/2 w-full relative group cursor-default">
            {/* Glow behind the chat box using Lobab Red */}
            <div className="absolute inset-0 bg-primary-600 rounded-3xl transform rotate-3 scale-105 opacity-10 dark:opacity-20 blur-2xl transition-all duration-500 group-hover:rotate-6 group-hover:opacity-20 dark:group-hover:opacity-30"></div>

            {/* Glassmorphic Chat Container */}
            <div className="bg-white/70 dark:bg-zinc-900/80 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-lg p-6 md:p-10 relative shadow-[0_10px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.3)] transition-colors duration-500 group-hover:border-primary-500/30 dark:group-hover:border-[#e00000]/50">

              <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200 dark:border-white/10 transition-colors duration-500">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-slate-100 dark:bg-zinc-800 border border-slate-300 dark:border-white/10 rounded-full flex items-center justify-center shadow-inner relative transition-colors duration-500">
                    <span className="text-2xl font-extrabold text-slate-500 dark:text-zinc-400">ع</span>
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-zinc-900 rounded-full transition-colors duration-500"></span>
                  </div>
                  <div>
                    <h5 className="font-bold text-lg text-slate-900 dark:text-white transition-colors duration-500">العميل الافتراضي</h5>
                    <p className="text-lg text-slate-500 dark:text-zinc-400 font-medium transition-colors duration-500">شركة تقنية ناشئة</p>
                  </div>
                </div>
                <span className="bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-400 border border-primary-100 dark:border-primary-500/30 text-md font-bold px-4 py-1.5 rounded-full flex flex-col md:flex-row items-center gap-2 transition-colors duration-500">
                  <CheckCircle2 className="w-3.5 h-3.5 hidden sm:block" />
                  <span className="hidden sm:block">متصل الآن</span>
                  <span className="sm:hidden">متصل</span>
                </span>
              </div>

              <div className="space-y-6">
                {/* Client Message */}
                <div className="flex justify-end">
                  <div className="bg-slate-50 dark:bg-zinc-800/80 p-5 rounded-2xl rounded-tr-sm border border-slate-200 dark:border-white/5 shadow-sm md:w-4/5 text-lg md:text-base backdrop-blur-md transition-colors duration-500">
                    <p className="text-slate-700 dark:text-zinc-200 leading-relaxed transition-colors duration-500">مرحباً، لقد أرسلت لك ملف الـ Brief الخاص بمشروعنا الجديد. نحن نبحث عن هوية تعكس التطور والثقة.</p>
                  </div>
                </div>

                {/* Designer Message */}
                <div className="flex justify-start">
                  <div className="bg-primary-50 dark:bg-primary-600/30 p-5 rounded-2xl rounded-tl-sm border border-primary-100 dark:border-primary-500/40 shadow-sm md:w-4/5 text-lg md:text-base backdrop-blur-md relative overflow-hidden transition-colors duration-500">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/50 dark:from-white/5 to-transparent pointer-events-none transition-colors duration-500"></div>
                    <p className="text-primary-900 dark:text-primary-50 leading-relaxed transition-colors duration-500">أهلاً بك. تم استلام الملف، سأقوم بدراسة المنافسين وتجهيز المفهوم المبدئي (Concept) خلال اليومين القادمين.</p>
                  </div>
                </div>

                {/* Typing Indicator */}
                <div className="flex justify-end mt-4">
                  <div className="bg-slate-50 dark:bg-zinc-800/50 px-4 py-3 rounded-2xl rounded-tr-sm border border-slate-200 dark:border-white/5 flex gap-1.5 items-center w-fit transition-colors duration-500">
                    <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-zinc-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-zinc-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span className="w-1.5 h-1.5 bg-slate-400 dark:bg-zinc-500 rounded-full animate-bounce"></span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
